import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient.ts';

// Types
export interface PartnerBalance {
  id: string;
  partner_user_id: string;
  current_balance: number;
  total_credits_purchased: number;
  total_credits_used: number;
  auto_refill_enabled: boolean;
  auto_refill_threshold: number;
  auto_refill_amount: number;
  stripe_payment_method_id?: string;
  last_refill_at?: string;
  created_at: string;
  updated_at: string;
}

export interface BalanceUpdateData {
  auto_refill_enabled?: boolean;
  auto_refill_threshold?: number;
  auto_refill_amount?: number;
  stripe_payment_method_id?: string;
}

export interface CreditOperationData {
  credits: number;
  description?: string;
}

// API functions
const fetchPartnerBalance = async (): Promise<PartnerBalance> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-balance?action=balance`, {
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch partner balance');
  }

  return response.json();
};

const updatePartnerBalance = async (data: BalanceUpdateData): Promise<PartnerBalance> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-balance?action=settings`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update partner balance');
  }

  return response.json();
};

const addPartnerCredits = async (data: CreditOperationData): Promise<{ success: boolean; balance: PartnerBalance }> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-balance?action=add`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add credits');
  }

  return response.json();
};

const deductPartnerCredits = async (data: CreditOperationData): Promise<{ success: boolean; balance: PartnerBalance }> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-balance?action=deduct`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to deduct credits');
  }

  return response.json();
};

// Hooks
export const usePartnerBalance = () => {
  return useQuery({
    queryKey: ['partner', 'balance'],
    queryFn: fetchPartnerBalance,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useUpdatePartnerBalance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePartnerBalance,
    onSuccess: () => {
      // Invalidate and refetch balance data
      queryClient.invalidateQueries({ queryKey: ['partner', 'balance'] });
    },
  });
};

export const useAddPartnerCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addPartnerCredits,
    onSuccess: () => {
      // Invalidate and refetch balance data
      queryClient.invalidateQueries({ queryKey: ['partner', 'balance'] });
    },
  });
};

export const useDeductPartnerCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deductPartnerCredits,
    onSuccess: () => {
      // Invalidate and refetch balance data
      queryClient.invalidateQueries({ queryKey: ['partner', 'balance'] });
    },
  });
};

// Helper hook for balance usage statistics
export const useBalanceStats = () => {
  const { data: balance, ...rest } = usePartnerBalance();
  
  const calculateMonthlyUsage = (balance: PartnerBalance): number => {
    // TODO: This should be calculated from actual usage data
    // For now, estimate based on total used - rough monthly average
    const estimatedMonthlyUsage = Math.round((balance.total_credits_used || 0) * 0.15); // Rough estimate
    return Math.max(0, estimatedMonthlyUsage);
  };

  const calculateProjectedMonthEnd = (currentBalance: number, monthlyUsage: number): number => {
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const daysPassed = today.getDate();
    const daysRemaining = daysInMonth - daysPassed;
    
    if (daysRemaining <= 0 || monthlyUsage <= 0) return currentBalance;
    
    const dailyUsage = monthlyUsage / daysInMonth;
    const projectedUsage = dailyUsage * daysRemaining;
    
    return Math.max(0, Math.round(currentBalance - projectedUsage));
  };

  return {
    ...rest,
    data: balance ? {
      currentBalance: balance.current_balance || 0,
      monthlyUsage: calculateMonthlyUsage(balance),
      projectedMonthEnd: calculateProjectedMonthEnd(balance.current_balance || 0, calculateMonthlyUsage(balance)),
      totalPurchased: balance.total_credits_purchased || 0,
      totalUsed: balance.total_credits_used || 0,
      autoRefillEnabled: balance.auto_refill_enabled || false,
      autoRefillThreshold: balance.auto_refill_threshold || 100,
      autoRefillAmount: balance.auto_refill_amount || 1000,
      lowBalanceWarning: (balance.current_balance || 0) < (balance.auto_refill_threshold || 100),
    } : undefined
  };
};
